import { useState } from 'react';
import { LinearProgress, TextField } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { FieldValidation } from '@rjsf/utils';
import { Squad360ServiceApiRef, ClusterServiceApiRef } from '../../../services';

type ProjectAndNamepsace = {
  name: string;
  namespace: string;
};

const filterNamespaces = async (projectsAndNamespaces: ProjectAndNamepsace[], ownSupportedApps: any[]) => {
  const appShortNames = ownSupportedApps.map((archerApp: any) => archerApp?.shortName.toLowerCase()).filter(Boolean);
  const returnedNamespaces = projectsAndNamespaces
    .filter(object =>
      appShortNames.some(appShortName => object.namespace.includes(appShortName) || object.name.includes(appShortName)),
    )
    .map(object => object.namespace);

  return returnedNamespaces;
};

export const Namespace = ({
  cluster,
  formData,
  onChange,
}: {
  cluster: string;
  formData: string;
  onChange: (namespace: string) => void;
}) => {
  const squad360 = useApi(Squad360ServiceApiRef);
  const [selectedNamespace, setSelectedNamespace] = useState<string>(formData);
  const clusterService = useApi(ClusterServiceApiRef);

  const getNamespaces = useAsync(async () => {
    if (!cluster) {
      return { availableNamespaces: [] };
    }
    try {
      const projectsAndNamespaces: ProjectAndNamepsace[] = await clusterService.getProjectsByCluster(cluster);
      const ownSupportedApps = await squad360.getOwnSupportedApps();
      if (projectsAndNamespaces.length > 0) {
        const filteredNamespacesResp = await filterNamespaces(projectsAndNamespaces, ownSupportedApps);
        return { availableNamespaces: filteredNamespacesResp };
      }
    } catch (error: any) {
      throw new Error('Cannot retrieve namespaces');
    }
    return { availableNamespaces: [] };
  }, [cluster]);

  return (
    <>
      <Autocomplete
        id="namespace"
        data-testid="namespace_selector"
        aria-label="namespace-dropdown"
        options={getNamespaces.value?.availableNamespaces || []}
        value={selectedNamespace}
        onChange={(_, namespace: string | null) => {
          setSelectedNamespace(namespace as string);
          onChange(namespace as string);
        }}
        getOptionLabel={option => option}
        getOptionSelected={option => !!option}
        style={{ width: '100%' }}
        renderInput={params => <TextField required {...params} label="Rancher Namespace" variant="outlined" />}
      />
      {getNamespaces.loading && (
        <LinearProgress
          style={{
            marginTop: 30,
            marginLeft: 10,
            marginRight: 10,
            width: '100%',
          }}
        />
      )}
      {getNamespaces.error && <Alert severity="error">Cannot get namespaces for cluster {cluster}</Alert>}
    </>
  );
};

export const namespaceValidation = (value: string, validation: FieldValidation) => {
  if (!value) {
    validation.addError('Please select a namespace');
  }
};
